import { formatMoney, formatPlacedDate, formatPlacedTime } from './format';
import {
  addonLineTotal,
  humanOrderStatus,
  orderDisplayNumber,
  pickDeliveryAddress,
  pickDeliveryContactPhone,
  pickDeliveryCoordinates,
  pickDeliveryFee,
  pickDeliveryRecipientName,
  pickCustomerName,
  pickCustomerPhone,
  pickFulfillmentLabel,
  pickLineItemAddons,
  pickLineItems,
  pickOrderLevelAddons,
  pickPaymentSummary,
  pickTotalAmount,
} from './orders';

const PARTNER_PHONE_STORAGE_KEY = 'qmpp.admin.deliveryPartnerWhatsApp';

/** @returns {string} Last saved delivery-partner WhatsApp number from localStorage. */
export function getSavedDeliveryPartnerPhone() {
  try {
    return localStorage.getItem(PARTNER_PHONE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** @param {string} phone */
export function saveDeliveryPartnerPhone(phone) {
  try {
    localStorage.setItem(PARTNER_PHONE_STORAGE_KEY, String(phone || '').trim());
  } catch {
    /* ignore */
  }
}

/**
 * Digits-only international number for wa.me (India: 91 + 10 digits).
 * @returns {string | null}
 */
export function normalizeWhatsAppPhone(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 10) digits = `91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return digits;
  if (digits.length >= 11 && digits.length <= 15) return digits;
  return null;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @returns {string}
 */
export function buildGoogleMapsUrl(lat, lng) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

function lineItemLabel(li) {
  if (!li || typeof li !== 'object') return 'Item';
  return li.name ?? li.title ?? li.menuItemName ?? li.itemName ?? li.productName ?? 'Item';
}

function lineItemQty(li) {
  const q = li?.quantity ?? li?.qty ?? li?.count ?? 1;
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function lineItemTotal(li) {
  const t = li?.lineTotal ?? li?.subtotal ?? li?.total ?? li?.amount;
  if (t != null) {
    const n = Number(t);
    if (Number.isFinite(n)) return n;
  }
  const u = li?.unitPrice ?? li?.price ?? li?.rate;
  if (u != null) {
    const n = Number(u);
    if (Number.isFinite(n)) return n * lineItemQty(li);
  }
  return null;
}

/**
 * Plain-text order summary for WhatsApp (wa.me prefill).
 */
export function buildOrderWhatsAppMessage(order) {
  if (!order || typeof order !== 'object') return '';

  const displayNo = orderDisplayNumber(order);
  const status = String(order.status || '').toUpperCase();
  const fulfillment = pickFulfillmentLabel(order);
  const customerName = pickCustomerName(order);
  const customerPhone = pickCustomerPhone(order);
  const deliveryContact = pickDeliveryContactPhone(order);
  const recipientName = pickDeliveryRecipientName(order);
  const address = pickDeliveryAddress(order);
  const coords = pickDeliveryCoordinates(order);
  const total = pickTotalAmount(order);
  const deliveryFee = pickDeliveryFee(order);
  const paymentSummary = pickPaymentSummary(order);
  const lineItems = pickLineItems(order);
  const orderLevelAddons = pickOrderLevelAddons(order);
  const placedAt = order.placedAt ?? order.createdAt ?? order.orderedAt;
  const notes = order.notes ?? order.customerNotes ?? order.specialInstructions ?? '';

  // Delivery partner needs the name of the person at the delivery address.
  // We prioritize the delivery address recipient name over the default account name ("user").
  const nameForPartner =
    recipientName ||
    (customerName && customerName.toLowerCase() !== 'user' ? customerName : recipientName || customerName);

  const lines = [];
  lines.push(`*Order ${displayNo}*`);
  lines.push(`Status: ${humanOrderStatus(status)}`);
  if (fulfillment) lines.push(`Type: ${fulfillment}`);
  if (placedAt) {
    lines.push(`Placed: ${formatPlacedDate(placedAt)} ${formatPlacedTime(placedAt)}`);
  }
  lines.push('');

  lines.push('*Customer*');
  lines.push(nameForPartner ? `Name: ${nameForPartner}` : 'Name: —');
  const phoneForPartner = deliveryContact || customerPhone;
  if (phoneForPartner) lines.push(`Phone: ${phoneForPartner}`);
  if (customerPhone && phoneForPartner && customerPhone !== phoneForPartner) {
    lines.push(`Alt Phone: ${customerPhone}`);
  }
  lines.push('');

  if (lineItems.length > 0 || orderLevelAddons.length > 0) {
    lines.push('*Items*');
    for (const li of lineItems) {
      const qty = lineItemQty(li);
      const label = lineItemLabel(li);
      const totalLine = lineItemTotal(li);
      const pricePart = totalLine != null ? ` — ${formatMoney(totalLine)}` : '';
      lines.push(`${qty}× ${label}${pricePart}`);
      for (const ad of pickLineItemAddons(li)) {
        const adTotal = addonLineTotal(ad, qty);
        lines.push(`  + ${ad.name}${adTotal != null ? ` (${formatMoney(adTotal)})` : ''}`);
      }
    }
    for (const ad of orderLevelAddons) {
      const adTotal = addonLineTotal(ad, 1);
      lines.push(`+ ${ad.name} (order)${adTotal != null ? ` — ${formatMoney(adTotal)}` : ''}`);
    }
    lines.push('');
  }

  if (fulfillment !== 'Pickup' && deliveryFee != null && Number(deliveryFee) > 0) {
    lines.push(`Delivery Fee: ${formatMoney(deliveryFee)}`);
  }
  if (total != null) lines.push(`*Total:* ${formatMoney(total)}`);
  if (paymentSummary?.method) {
    lines.push(`Payment: ${paymentSummary.method}`);
  }
  lines.push('');

  if (address && fulfillment !== 'Pickup') {
    lines.push('*Deliver to*');
    if (address.label) lines.push(address.label);
    for (const ln of address.lines) lines.push(ln);
    lines.push('');
  } else if (fulfillment === 'Pickup') {
    lines.push('*Pickup* — customer collects at store.');
    lines.push('');
  }

  if (coords) {
    const { lat, lng } = coords;
    lines.push('*Location*');
    lines.push(`Lat: ${lat}, Lng: ${lng}`);
    lines.push(buildGoogleMapsUrl(lat, lng));
    lines.push('');
  }

  if (notes) {
    lines.push('*Notes*');
    lines.push(String(notes));
  }

  return lines.join('\n').trim();
}

/**
 * Opens WhatsApp (app or web) with a pre-filled message — no paid API.
 * @returns {{ ok: true, url: string } | { ok: false, error: string }}
 */
export function openWhatsAppWithMessage(rawPhone, message) {
  const phone = normalizeWhatsAppPhone(rawPhone);
  if (!phone) {
    return { ok: false, error: 'Enter a valid mobile number (10 digits or with country code).' };
  }
  const text = String(message || '').trim();
  if (!text) {
    return { ok: false, error: 'Nothing to send for this order.' };
  }
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
  return { ok: true, url };
}
